import Cookies from 'js-cookie';
import { graphql } from 'gatsby';
import type { ABTestResult } from './getABTest';
import { type ABTestComponentConfig } from '../../../netlify/edge-functions/lib/types';
import {
  CONTROL,
  A_B_TEST_COOKIE_HOST_PATH,
  COOKIE_PREFIX_COMPONENT_TEST,
} from '../../../netlify/edge-functions/lib/constants';
import {
  getRandomVariant,
  shouldExecuteABTest,
} from '../../../netlify/edge-functions/lib/shared-functions';
import getFirstPartyData from './getFirstPartyData';
import errorLogger from '../errorLogger';

export type ComponentABTestedPage =
  | Queries.GeneralPageTemplateQuery['kontentItemGeneralPage']
  | Queries.ArticlePageTemplateQuery['kontentItemArticlePage'];

interface ABTestComponentResult extends Omit<ABTestResult, 'elements'> {
  elements: NonNullable<ComponentABTestedPage>['elements'];
}

type BuildingBlocks = NonNullable<
  NonNullable<ComponentABTestedPage>['elements']['building_blocks']
>['value'][number][];

const getABTestComponent = ({
  page,
  processedABTestComponentsConfig,
  allKontentItemComponentABTest,
}: {
  page: ComponentABTestedPage;
  processedABTestComponentsConfig: ABTestComponentConfig[];
  allKontentItemComponentABTest: Queries.ComponentABTestQuery['allKontentItemComponentABTest']['nodes'];
}): ABTestComponentResult => {
  const controlElements = page!.elements;

  // NOTE Result initialization
  const result: ABTestComponentResult = {
    elements: controlElements,
    abTestId: null,
    abTestVariantId: null,
    abTestComponents: [],
  };

  if (globalThis.window === undefined || processedABTestComponentsConfig.length === 0) {
    return result;
  }

  return generateBuildingBlocks(
    page,
    processedABTestComponentsConfig,
    allKontentItemComponentABTest
  );
};

const generateBuildingBlocks = (
  page: ComponentABTestedPage,
  abTestComponentsConfig: ABTestComponentConfig[],
  allKontentItemComponentABTest: Queries.ComponentABTestQuery['allKontentItemComponentABTest']['nodes']
) =>
  (page?.elements.building_blocks?.value ?? []).reduce(
    (result: ABTestComponentResult, block: BuildingBlocks[number]) => {
      const pushDefaultBlock = () => {
        (result.elements.building_blocks!.value as BuildingBlocks).push(block);
      };

      // NOTE: Get campaignId from the current block, the data from component a/b test from kontent and the config generated in the edge function file
      const abTestCampaignId = getABTestCampaignId(block);
      const dataABTest = findABTestData(allKontentItemComponentABTest, abTestCampaignId);
      const abTestConfig = abTestComponentsConfig.find((config) => config.id === abTestCampaignId);
      // Get the assigned variantId from the cookie
      const cookieName = `${COOKIE_PREFIX_COMPONENT_TEST}${abTestCampaignId}`;
      const existingCookie = Cookies.get(cookieName);
      const hasCookie = Boolean(existingCookie);

      if (!abTestCampaignId || !dataABTest || !abTestConfig) {
        if (hasCookie) {
          Cookies.remove(cookieName);
        }
        pushDefaultBlock();
        return result;
      }

      const firstPartyData = getFirstPartyData();
      const shouldExecute = shouldExecuteABTest({
        personalizations: abTestConfig.personalizations,
        logicalOperator: abTestConfig.logicalOperator,
        firstPartyData,
        isReturningVisitor: hasCookie,
      });

      // The A/B Test has personalisation and criteria are not met (bypassed by returning visitor)
      if (!shouldExecute) {
        pushDefaultBlock();
        return result;
      }

      const chosenVariantId = existingCookie ?? getRandomVariant(abTestConfig.variants)?.id;

      // NOTE Something went wrong in the variant selection process
      if (!chosenVariantId) {
        errorLogger(
          new Error(
            `getABTest: Could not get a random variant for the page with abTestId: ${abTestConfig.id}`
          )
        );
        pushDefaultBlock();
        return result;
      }

      if (!hasCookie) {
        Cookies.set(cookieName, chosenVariantId, {
          path: A_B_TEST_COOKIE_HOST_PATH,
          expires: new Date(abTestConfig.endDate),
        });
      }

      result.abTestComponents.push({
        campaignId: abTestCampaignId,
        variantId: chosenVariantId,
      });

      if (chosenVariantId === CONTROL) {
        pushDefaultBlock();
        return result;
      }

      const variant = dataABTest.elements.variants.value.find(
        (variant) => variant.elements.name.value === chosenVariantId
      );

      if (variant) {
        (result.elements.building_blocks!.value as BuildingBlocks).push(
          variant.elements.component_variant.value
        );
      } else {
        errorLogger(
          new Error(
            `getABTest: Could not find the variant with id: ${chosenVariantId} for the page with abTestId: ${abTestConfig.id}`
          )
        );
        pushDefaultBlock();
      }

      return result;
    },
    {
      elements: {
        ...page?.elements,
        building_blocks: {
          value: [] as BuildingBlocks,
        },
      },
      abTestId: null,
      abTestVariantId: null,
      abTestComponents: [],
    } as ABTestResult
  );

export const getABTestCampaignId = (block: BuildingBlocks[number]): string => {
  // NOTE: Find the entry that contains the A/B test campaign
  const elementsProperty = Object.values(block).find((value) => {
    return (
      value &&
      typeof value === 'object' &&
      'component_a_b_test_personalization_snippet__a_b_test_campaign' in value
    );
  }) as
    | {
        component_a_b_test_personalization_snippet__a_b_test_campaign?: {
          value?: { elements: { name: { value: string } } } | null;
        } | null;
      }
    | undefined;

  return (
    elementsProperty?.component_a_b_test_personalization_snippet__a_b_test_campaign?.value?.elements
      .name.value || ''
  );
};

export const findABTestData = (
  allKontentItemComponentABTest: Queries.ComponentABTestQuery['allKontentItemComponentABTest']['nodes'],
  abTestCampaignId: string
) => {
  return allKontentItemComponentABTest.find(
    (abTest) => abTest.elements.name.value === abTestCampaignId
  );
};

export const fragment = graphql`
  fragment COMPONENT_A_B_TEST on kontent_snippet_component_a_b_test_personalization_snippet {
    component_a_b_test_personalization_snippet__a_b_test_campaign {
      value {
        elements {
          name {
            value
          }
        }
      }
    }
  }
`;

export default getABTestComponent;
